import Button from "../../../shared/components/Button";
import Spinner from "../../../shared/components/Spinner";
import { siteTexts } from "../../../shared/i18n/siteTexts";
import type {
  DraftPlayer,
  DraftSideChoice,
} from "../../challenges/types/challengeDraft";
import {
  draftSideToChoice,
  normalizeCpf,
} from "../../challenges/utils/challengePairs";
import {
  formatPaymentStatusLabel,
  formatRegistrationStatusLabel,
} from "../../events/shared/utils/eventRegistrationFormatters";
import type { AdminEventRegistration } from "../services/eventRegistrationAdminService";
import AdminRegistrationRowActions from "./AdminRegistrationRowActions";

type PaymentMethod = "pix" | "credit_card" | "debit_card";

type AdminSelectedPlayersListProps = {
  adminPaymentMethod: PaymentMethod;
  isAdmin: boolean;
  loading: boolean;
  onCopyPaymentLink: (
    id: number,
    paymentMethod?: PaymentMethod,
    existingPaymentLink?: string | null,
  ) => void;
  onDeleteRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onEditAvailabilityRestriction?: (registration: AdminEventRegistration) => void;
  onMarkPaidRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onRefresh: () => void;
  onRejectRegistration: (
    id: number,
    registration?: AdminEventRegistration | null,
  ) => void;
  onRemovePlayer: (cpf: string) => void;
  onUpdatePlayerSide: (cpf: string, sideChoice: DraftSideChoice) => void;
  players: DraftPlayer[];
  registrationByCpf: Record<string, AdminEventRegistration>;
};

export default function AdminSelectedPlayersList({
  adminPaymentMethod,
  isAdmin,
  loading,
  onCopyPaymentLink,
  onDeleteRegistration,
  onEditAvailabilityRestriction,
  onMarkPaidRegistration,
  onRefresh,
  onRejectRegistration,
  onRemovePlayer,
  onUpdatePlayerSide,
  players,
  registrationByCpf,
}: AdminSelectedPlayersListProps) {
  const t = siteTexts.admin;

  const restrictionSummary = (registration: AdminEventRegistration) => {
    const details = registration.availabilityRestriction?.details || [];
    const labels = details
      .map((item) => String(item.label || "").trim())
      .filter(Boolean);
    const fallback = String(registration.availabilityRestriction?.label || "").trim();
    const summary = labels.length > 0 ? labels.join(" • ") : fallback;
    return registration.availabilityRestriction?.hasRestriction && summary
      ? summary
      : "";
  };

  return (
    <details className="lista-duplas lista-duplas-collapsible" open>
      <summary>
        <h4>{siteTexts.challengeEngine.labels.registeredPlayers}</h4>
        <button
          type="button"
          className="admin-summary-refresh-button"
          aria-label={t.historyList.refreshRegistrations}
          title={t.historyList.refreshRegistrations}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onRefresh();
          }}
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 5v6h-6" />
            <path d="M20 11a8 8 0 1 0 2 5.3" />
          </svg>
        </button>
      </summary>

      {loading && (
        <p className="status-message">
          <Spinner label={t.historyList.loadingChallengeRegistrations} />
        </p>
      )}

      {players.length === 0 ? (
        <p className="vazio">
          {siteTexts.challengeEngine.labels.nonePlayerAdded}
        </p>
      ) : (
        players.map((player) => {
          const cpf = normalizeCpf(player.cpf);
          const registration = registrationByCpf[cpf] || null;
          const sideChoice = draftSideToChoice(player.side) || "ambos";
          return (
            <div key={player.cpf} className="jogador-item">
              <div className="challenge-registration-row-main">
                <span>{player.name}</span>
                {registration && (
                  <>
                    <p className="challenge-registration-row-status">
                      {t.eventLink.registration}:{" "}
                      <strong>
                        {formatRegistrationStatusLabel(registration.status)}
                      </strong>
                      {" · "}
                      {t.registrationsTab.payment}:{" "}
                      <strong>
                        {registration.paymentRequired
                          ? formatPaymentStatusLabel(registration.paymentStatus)
                          : t.registrationsTab.notRequired}
                      </strong>
                    </p>
                    {restrictionSummary(registration) && (
                      <p className="challenge-registration-row-status challenge-registration-row-status--restriction">
                        Restrição:{" "}
                        <strong>{restrictionSummary(registration)}</strong>
                      </p>
                    )}
                  </>
                )}
              </div>
              <div className="challenge-player-side-controls">
                <div
                  className="lado-opcoes side-choice"
                  role="group"
                  aria-label={t.historyList.sidePreference}
                >
                  {(["direita", "esquerda", "ambos"] as const).map((side) => (
                    <Button
                      key={`${cpf}-side-${side}`}
                      type="button"
                      variant="pill"
                      className={sideChoice === side ? "active" : ""}
                      onClick={() => onUpdatePlayerSide(player.cpf, side)}
                    >
                      {side === "direita"
                        ? siteTexts.challengeEngine.labels.right
                        : side === "esquerda"
                          ? siteTexts.challengeEngine.labels.left
                          : siteTexts.challengeEngine.labels.rightAndLeft}
                    </Button>
                  ))}
                </div>
              </div>
              {registration ? (
                <AdminRegistrationRowActions
                  adminPaymentMethod={adminPaymentMethod}
                  isAdmin={isAdmin}
                  onCopyPaymentLink={onCopyPaymentLink}
                  onDeleteRegistration={onDeleteRegistration}
                  onEditAvailabilityRestriction={onEditAvailabilityRestriction}
                  onMarkPaidRegistration={onMarkPaidRegistration}
                  onRejectRegistration={onRejectRegistration}
                  registration={registration}
                  showCancelAction
                />
              ) : (
                <div className="challenge-registration-row-actions">
                  <Button type="button" variant="pill" disabled>
                    Processando inscrição...
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={() => onRemovePlayer(player.cpf)}
                  >
                    {siteTexts.challengeEngine.labels.remove}
                  </Button>
                </div>
              )}
            </div>
          );
        })
      )}
    </details>
  );
}
